import { ApiError, fieldErrorsFromBody } from "@/lib/api-types";
import { awaitAuthToken } from "@/lib/auth-token";
import { env } from "@/lib/env";
import { logger } from "@/lib/logger";

/**
 * The one typed HTTP client. Components never call this directly — only the
 * repository layer does (ARCHITECTURE.md §3/§4). It:
 *  - joins paths onto NEXT_PUBLIC_BACKEND (which already carries the backend
 *    mount, e.g. `.../server`); callers pass `/api/v1/...` or bare `/login`,
 *  - attaches `Authorization: Bearer <token>` when a session token is present,
 *    waiting first if the NextAuth session is still bootstrapping so a request
 *    can't race ahead of the token and come back 401 (see `auth-token.ts`),
 *  - sends a per-request `x-request-id` for log/trace correlation,
 *  - normalizes every non-2xx into an `ApiError`.
 */
export interface HttpClient {
  get<T>(path: string, init?: RequestInit): Promise<T>;
  post<T>(path: string, body?: unknown, init?: RequestInit): Promise<T>;
  put<T>(path: string, body?: unknown, init?: RequestInit): Promise<T>;
  patch<T>(path: string, body?: unknown, init?: RequestInit): Promise<T>;
  del<T>(path: string, init?: RequestInit): Promise<T>;
}

interface HttpClientOptions {
  baseUrl?: string;
  /** May resolve asynchronously; the default waits out the session bootstrap. */
  getToken?: () => string | null | Promise<string | null>;
}

function joinUrl(baseUrl: string, path: string) {
  if (/^https?:\/\//i.test(path)) return path;
  return `${baseUrl.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
}

function randomRequestId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `req_${Math.random().toString(36).slice(2)}`;
}

export function createHttpClient(options: HttpClientOptions = {}): HttpClient {
  const baseUrl = options.baseUrl ?? env.backendUrl;
  const getToken = options.getToken ?? awaitAuthToken;

  async function request<T>(
    method: string,
    path: string,
    body?: unknown,
    init: RequestInit = {},
  ): Promise<T> {
    const url = joinUrl(baseUrl, path);
    const requestId = randomRequestId();
    const token = await getToken();

    const headers = new Headers(init.headers);
    headers.set("x-request-id", requestId);
    if (token) headers.set("Authorization", `Bearer ${token}`);

    const hasBody = body !== undefined && !(body instanceof FormData);
    if (hasBody && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }

    let response: Response;
    try {
      response = await fetch(url, {
        ...init,
        method,
        headers,
        body:
          body instanceof FormData
            ? body
            : hasBody
              ? JSON.stringify(body)
              : init.body,
      });
    } catch (cause) {
      logger.error("network request failed", { method, path, requestId });
      throw new ApiError(0, "Network request failed", { requestId });
    }

    const payload = await parseBody(response);

    if (!response.ok) {
      // Prefer the backend's own words, in the order it prefers them: a plain
      // `error`/`message` string, then the first entry of a per-field
      // `errors[]`/`msg[]` payload. Without that last step a validation
      // rejection — the one class of failure the user can actually act on —
      // reaches the UI as "Request failed with status 422". Surfaces that can
      // render every field error read `getApiFieldErrors(error)` instead; this
      // is the single-line fallback for those that only show one.
      const message =
        (isRecord(payload) &&
          (asString(payload.error) ?? asString(payload.message))) ||
        fieldErrorsFromBody(payload)[0]?.message ||
        `Request failed with status ${response.status}`;
      logger.warn("http error", {
        method,
        path,
        status: response.status,
        requestId,
      });
      throw new ApiError(response.status, message, {
        body: payload,
        requestId,
      });
    }

    return payload as T;
  }

  return {
    get: (path, init) => request("GET", path, undefined, init),
    post: (path, body, init) => request("POST", path, body, init),
    put: (path, body, init) => request("PUT", path, body, init),
    patch: (path, body, init) => request("PATCH", path, body, init),
    del: (path, init) => request("DELETE", path, undefined, init),
  };
}

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204) return null;
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

/** Default singleton used by repositories in the app. */
export const http = createHttpClient();
